import {
  readWorkspaceDir,
  readWorkspaceFile,
  statWorkspacePath,
} from "../workbench/fs.js";
import type { RunnerMode, TagContextLink, TaskContext } from "@project/shared";

interface ResolvedEntry {
  type: "rule" | "file" | "folder" | "doc";
  path: string;
  label?: string;
  /** One-line summary shown in the discovery blurb (never the full body). */
  summary: string | null;
}

interface ResolvedTagContext {
  tagName: string;
  description: string | null;
  entries: ResolvedEntry[];
  /** The tag carries a full glossary overview — worth a get_tag call. */
  hasOverview?: boolean;
  /** Repo file the overview is sourced from — Read it from the checkout
   *  (branch-correct) instead of get_tag (base-branch materialization). */
  overviewPath?: string | null;
}

const TYPE_PRIORITY: Record<string, number> = { rule: 0, doc: 1, file: 2, folder: 3 };

// A single reference is described by one short line — no full-file bodies are
// ever injected. This caps how much of a file we scan to derive that line.
const SUMMARY_SCAN_CHARS = 4_000;
const SUMMARY_MAX_CHARS = 160;

// Binary file extensions to skip content injection
const BINARY_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".ico",
  ".svg",
  ".bmp",
  ".mp3",
  ".mp4",
  ".wav",
  ".avi",
  ".mov",
  ".pdf",
  ".zip",
  ".tar",
  ".gz",
  ".woff",
  ".woff2",
  ".ttf",
  ".eot",
  ".otf",
  ".exe",
  ".dll",
  ".so",
  ".dylib",
  ".wasm",
]);

function isBinaryPath(filePath: string): boolean {
  const ext = filePath.slice(filePath.lastIndexOf(".")).toLowerCase();
  return BINARY_EXTENSIONS.has(ext);
}

// Module-scope cache of derived summaries keyed by absolute path.
// Entries are invalidated when mtime changes (post-merge rule updates)
// and can be cleared wholesale on session boundaries via clearTagContextCache().
const fileSummaryCache = new Map<string, { mtimeMs: number; summary: string | null }>();
const folderListingCache = new Map<string, { mtimeMs: number; listing: string }>();
let fileReadCount = 0;
let folderReadCount = 0;

export function clearTagContextCache(): void {
  fileSummaryCache.clear();
  folderListingCache.clear();
}

export function _getTagContextCacheStatsForTest(): {
  files: number;
  folders: number;
  fileReads: number;
  folderReads: number;
} {
  return {
    files: fileSummaryCache.size,
    folders: folderListingCache.size,
    fileReads: fileReadCount,
    folderReads: folderReadCount,
  };
}

export function _resetTagContextReadCountsForTest(): void {
  fileReadCount = 0;
  folderReadCount = 0;
}

/**
 * Derive a one-line description for a reference file WITHOUT injecting its body.
 * Preference order: frontmatter `description:` / `title:` → first markdown
 * heading → first non-empty content line. YAML frontmatter (loader directives
 * like `paths:`) is stripped so it never leaks into the prompt.
 */
export function deriveSummary(raw: string): string | null {
  let body = raw;
  let frontmatter = "";
  if (raw.startsWith("---")) {
    const end = raw.indexOf("\n---", 3);
    if (end !== -1) {
      frontmatter = raw.slice(3, end);
      const afterClose = raw.indexOf("\n", end + 1);
      body = afterClose === -1 ? "" : raw.slice(afterClose + 1);
    }
  }

  const fmDescription = frontmatter
    .split("\n")
    .map((l) => l.trim())
    .find((l) => /^(description|title):/i.test(l));
  if (fmDescription) {
    const value = fmDescription
      .slice(fmDescription.indexOf(":") + 1)
      .trim()
      .replace(/^["']|["']$/g, "");
    if (value) return truncateSummary(value);
  }

  for (const rawLine of body.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;
    // Strip markdown heading markers / list bullets for a clean one-liner.
    const cleaned = line
      .replace(/^#+\s*/, "")
      .replace(/^[-*]\s+/, "")
      .trim();
    if (cleaned) return truncateSummary(cleaned);
  }
  return null;
}

function truncateSummary(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length > SUMMARY_MAX_CHARS
    ? collapsed.slice(0, SUMMARY_MAX_CHARS - 1).trimEnd() + "…"
    : collapsed;
}

async function readFileSummary(filePath: string): Promise<string | null> {
  try {
    if (isBinaryPath(filePath)) return null;
    const st = await statWorkspacePath(filePath);
    if (!st.exists) return null;
    const mtimeMs = st.mtimeMs;
    const cached = fileSummaryCache.get(filePath);
    if (cached && cached.mtimeMs === mtimeMs) {
      return cached.summary;
    }
    const raw = await readWorkspaceFile(filePath);
    fileReadCount++;
    // Only scan a bounded prefix — enough to find a description/heading.
    const summary = deriveSummary(raw.slice(0, SUMMARY_SCAN_CHARS));
    fileSummaryCache.set(filePath, { mtimeMs, summary });
    return summary;
  } catch {
    return null;
  }
}

async function readFolderListing(folderPath: string): Promise<string | null> {
  try {
    const st = await statWorkspacePath(folderPath);
    if (!st.exists) return null;
    const mtimeMs = st.mtimeMs;
    const cached = folderListingCache.get(folderPath);
    if (cached && cached.mtimeMs === mtimeMs) {
      return cached.listing;
    }
    const entries = await readWorkspaceDir(folderPath);
    folderReadCount++;
    const listing = `Files: ${entries.join(", ")}`;
    folderListingCache.set(folderPath, { mtimeMs, listing });
    return listing;
  } catch {
    return null;
  }
}

async function resolveEntry(entry: {
  type: string;
  path: string;
  label?: string;
}): Promise<ResolvedEntry> {
  const result: ResolvedEntry = {
    type: entry.type as ResolvedEntry["type"],
    path: entry.path,
    label: entry.label,
    summary: null,
  };

  // A human-authored label is the best one-liner; skip disk I/O when present.
  if (entry.label) {
    result.summary = truncateSummary(entry.label);
    return result;
  }

  if (entry.type === "folder") {
    result.summary = await readFolderListing(entry.path);
    return result;
  }

  // rule, doc, or file — derive a one-line summary from disk (body is never
  // injected). Project docs are synced from the repo, so a doc path is a
  // workspace path too.
  result.summary = await readFileSummary(entry.path);
  return result;
}

function formatEntry(entry: ResolvedEntry): string {
  const suffix = entry.summary ? ` — ${entry.summary}` : "";
  return `- \`${entry.path}\`${suffix}`;
}

/**
 * Tags MENTIONED in the card's description/plan (not assigned to it): the
 * author deep-linked a glossary term — surface its meaning and where the
 * full spec lives, without auto-loading anything.
 */
function formatMentionedTags(mentioned?: ResolvedTagContext[]): string[] {
  if (!mentioned || mentioned.length === 0) return [];
  const parts: string[] = [
    `\n### Mentioned glossary terms`,
    `The card's description/plan deep-links these project tags:`,
  ];
  for (const tag of mentioned) {
    const desc = tag.description ? ` — ${tag.description}` : "";
    const hint = tag.overviewPath
      ? ` (full overview: Read \`${tag.overviewPath}\` in the checkout)`
      : tag.hasOverview
        ? ` (full overview: get_tag("${tag.tagName}"))`
        : "";
    parts.push(`- "${tag.tagName}"${desc}${hint}`);
    for (const entry of tag.entries) {
      parts.push(`  ${formatEntry(entry)}`);
    }
  }
  return parts;
}

/**
 * A reviewer reads far more than it edits, so the builder framing below ("they
 * auto-load when you edit matching files") describes a mechanism that never
 * fires for a review session and reads as "you can skip these". Reviews get
 * their own intro pointing at the same docs as the conventions the diff is
 * judged against.
 */
function tagContextIntro(runnerMode?: RunnerMode): string {
  if (runnerMode === "code-review") {
    return `These docs match this card's tags — they define the domain conventions the diff under review must follow. Read the entries relevant to the diff before judging pattern consistency; skip the ones the diff does not touch.`;
  }
  return `These docs match this task's tags. They auto-load when you edit matching files, and you can Read any of them the moment you need its detail — do NOT read them all up front.`;
}

function formatResolvedTags(
  resolved: ResolvedTagContext[],
  subProject?: { name: string; entries: ResolvedEntry[] } | null,
  mentioned?: ResolvedTagContext[],
  runnerMode?: RunnerMode,
): string {
  const parts: string[] = [
    `\n## Reference Guides (load on demand)`,
    tagContextIntro(runnerMode),
  ];

  for (const tag of resolved) {
    if (tag.entries.length === 0 && !tag.hasOverview) continue;
    const desc = tag.description ? ` — ${tag.description}` : "";
    parts.push(`\n### Tag: "${tag.tagName}"${desc}`);
    if (tag.overviewPath) {
      // Repo-sourced overview: the checkout copy is branch-correct and fresher
      // than the served base-branch materialization.
      parts.push(`- Full glossary overview — Read \`${tag.overviewPath}\` in the checkout`);
    } else if (tag.hasOverview) {
      parts.push(`- Full glossary overview available — call get_tag("${tag.tagName}")`);
    }
    for (const entry of tag.entries) {
      parts.push(formatEntry(entry));
    }
  }

  parts.push(...formatMentionedTags(mentioned));

  if (subProject && subProject.entries.length > 0) {
    parts.push(`\n### Sub-project: "${subProject.name}"`);
    for (const entry of subProject.entries) {
      parts.push(formatEntry(entry));
    }
  }

  return parts.join("\n");
}

async function resolveEntries(
  contextPaths: TagContextLink[] | null | undefined,
): Promise<ResolvedEntry[]> {
  if (!contextPaths?.length) return [];
  const sorted = [...contextPaths].sort(
    (a, b) => (TYPE_PRIORITY[a.type] ?? 99) - (TYPE_PRIORITY[b.type] ?? 99),
  );
  const results: ResolvedEntry[] = [];
  for (const entry of sorted) {
    results.push(await resolveEntry(entry));
  }
  return results;
}

function countResolved(entries: ResolvedEntry[]): { injected: number; skipped: number } {
  const injected = entries.filter((e) => e.summary !== null).length;
  return { injected, skipped: entries.length - injected };
}

async function resolveAssignedTags(
  assignedTags: NonNullable<TaskContext["projectTags"]>,
): Promise<{ resolved: ResolvedTagContext[]; injected: number; skipped: number }> {
  const resolved: ResolvedTagContext[] = [];
  let injected = 0;
  let skipped = 0;

  for (const tag of assignedTags) {
    const entries = await resolveEntries(tag.contextPaths);
    const counts = countResolved(entries);
    injected += counts.injected;
    skipped += counts.skipped;
    resolved.push({
      tagName: tag.name,
      description: tag.description,
      entries,
      hasOverview: tag.hasOverview,
      overviewPath: tag.overviewPath ?? null,
    });
  }

  return { resolved, injected, skipped };
}

export async function resolveTagContext(
  projectTags: TaskContext["projectTags"],
  taskTagIds: string[],
  _model: string,
  _betas?: string[],
  runnerMode?: RunnerMode,
  subProject?: { name: string; contextPaths: TagContextLink[] | null } | null,
  mentionedTagIds?: string[],
): Promise<{ injectedSection: string; stats: { injected: number; skipped: number } }> {
  const taskTagIdSet = new Set(taskTagIds);
  const assignedTags = (projectTags ?? []).filter((t) => taskTagIdSet.has(t.id));
  // Tags deep-linked in the card body but not assigned — glossary context only.
  const mentionedIdSet = new Set(mentionedTagIds ?? []);
  const mentionedTags = (projectTags ?? []).filter(
    (t) => mentionedIdSet.has(t.id) && !taskTagIdSet.has(t.id),
  );
  const hasTagPaths = assignedTags.some((t) => t.contextPaths?.length || t.hasOverview);
  const hasSubProjectPaths = (subProject?.contextPaths?.length ?? 0) > 0;

  if (!hasTagPaths && !hasSubProjectPaths && mentionedTags.length === 0) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  const {
    resolved,
    injected: tagInjected,
    skipped: tagSkipped,
  } = await resolveAssignedTags(assignedTags);
  const { resolved: mentionedResolved } = await resolveAssignedTags(mentionedTags);

  let subProjectResolved: { name: string; entries: ResolvedEntry[] } | null = null;
  let subInjected = 0;
  let subSkipped = 0;
  if (subProject && hasSubProjectPaths) {
    const entries = await resolveEntries(subProject.contextPaths);
    const counts = countResolved(entries);
    subInjected = counts.injected;
    subSkipped = counts.skipped;
    subProjectResolved = { name: subProject.name, entries };
  }

  return {
    injectedSection: formatResolvedTags(
      resolved,
      subProjectResolved,
      mentionedResolved,
      runnerMode,
    ),
    stats: { injected: tagInjected + subInjected, skipped: tagSkipped + subSkipped },
  };
}
